import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Search, Edit, Trash2, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";

const initialFormData = {
  codigo: "",
  nombre: "",
  identificacion_fiscal: "",
  persona_contacto: "",
  telefono: "",
  telefono_otro: "",
  email: "",
  email_otro: "",
  direccion: "",
  ciudad: "",
  provincia: "",
  codigo_postal: "",
  pais: "",
  comentarios: "",
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const { hasRole } = useAuth();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api.get("/clients");
      setClients(response.data);
    } catch (error) {
      toast.error("Error al cargar los clientes");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingId(client.id);
      setFormData({
        codigo: client.codigo || "",
        nombre: client.nombre || "",
        identificacion_fiscal: client.identificacion_fiscal || "",
        persona_contacto: client.persona_contacto || "",
        telefono: client.telefono || "",
        telefono_otro: client.telefono_otro || "",
        email: client.email || "",
        email_otro: client.email_otro || "",
        direccion: client.direccion || "",
        ciudad: client.ciudad || "",
        provincia: client.provincia || "",
        codigo_postal: client.codigo_postal || "",
        pais: client.pais || "",
        comentarios: client.comentarios || "",
      });
    } else {
      setEditingId(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre) {
      toast.error("El nombre del cliente es obligatorio");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/clients/${editingId}`, formData);
        toast.success("Cliente actualizado correctamente");
      } else {
        await api.post("/clients", formData);
        toast.success("Cliente creado correctamente");
      }
      fetchClients();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el cliente");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/clients/${deleteId}`);
      toast.success("Cliente eliminado correctamente");
      setClients(clients.filter((c) => c.id !== deleteId));
    } catch (error) {
      toast.error("Error al eliminar el cliente");
    } finally {
      setDeleteId(null);
    }
  };

  const filteredClients = clients.filter(
    (client) =>
      client.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      client.email?.toLowerCase().includes(search.toLowerCase()) ||
      client.identificacion_fiscal?.toLowerCase().includes(search.toLowerCase()) ||
      client.codigo?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Clientes</h1>
          <p className="text-slate-500 mt-1">Gestión de clientes y datos de contacto</p>
        </div>
        {hasRole("admin") && (
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="create-client-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Cliente
          </Button>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre, email, CIF o código..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-clients"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-sm border border-slate-200">
          <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No hay clientes</h3>
          <p className="text-slate-500 mb-4">
            {search ? "No se encontraron clientes con ese criterio" : "Comienza creando tu primer cliente"}
          </p>
          {hasRole("admin") && !search && (
            <Button onClick={() => handleOpenDialog()} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Crear Cliente
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Código
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Nombre
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  CIF/NIF
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Contacto
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Email
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  País
                </TableHead>
                {hasRole("admin") && (
                  <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider w-[100px]">
                    Acciones
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClients.map((client) => (
                <TableRow key={client.id} className="hover:bg-slate-50" data-testid={`client-row-${client.id}`}>
                  <TableCell className="font-mono text-slate-500">{client.codigo || "-"}</TableCell>
                  <TableCell className="font-medium">{client.nombre}</TableCell>
                  <TableCell className="font-mono">{client.identificacion_fiscal || "-"}</TableCell>
                  <TableCell>{client.persona_contacto || "-"}</TableCell>
                  <TableCell className="text-slate-500">{client.email || "-"}</TableCell>
                  <TableCell>{client.pais || "-"}</TableCell>
                  {hasRole("admin") && (
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenDialog(client)}
                          data-testid={`edit-client-${client.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 hover:text-red-700"
                          onClick={() => setDeleteId(client.id)}
                          data-testid={`delete-client-${client.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Cliente" : "Nuevo Cliente"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Código (ID)</Label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                  placeholder="Ej: C01"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Nombre de la empresa"
                  required
                  data-testid="input-client-nombre"
                />
              </div>
              <div className="space-y-2">
                <Label>Identificación Fiscal (CIF/NIF)</Label>
                <Input
                  value={formData.identificacion_fiscal}
                  onChange={(e) => setFormData({ ...formData, identificacion_fiscal: e.target.value })}
                  placeholder="Ej: B-72448970"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>País</Label>
                <Input
                  value={formData.pais}
                  onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                  placeholder="España"
                />
              </div>
              <div className="space-y-2">
                <Label>Persona de Contacto</Label>
                <Input
                  value={formData.persona_contacto}
                  onChange={(e) => setFormData({ ...formData, persona_contacto: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono Principal</Label>
                <Input
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Otro Teléfono</Label>
                <Input
                  value={formData.telefono_otro}
                  onChange={(e) => setFormData({ ...formData, telefono_otro: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email Principal</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contacto@empresa.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Otro Email</Label>
                <Input
                  type="email"
                  value={formData.email_otro}
                  onChange={(e) => setFormData({ ...formData, email_otro: e.target.value })}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Dirección</Label>
                <Input
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  placeholder="Calle, número, nave, etc."
                />
              </div>
              <div className="space-y-2">
                <Label>Ciudad</Label>
                <Input
                  value={formData.ciudad}
                  onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                  placeholder="Ej: Valencia"
                />
              </div>
              <div className="space-y-2">
                <Label>Provincia</Label>
                <Input
                  value={formData.provincia}
                  onChange={(e) => setFormData({ ...formData, provincia: e.target.value })}
                  placeholder="Ej: Valencia"
                />
              </div>
              <div className="space-y-2">
                <Label>Código Postal</Label>
                <Input
                  value={formData.codigo_postal}
                  onChange={(e) => setFormData({ ...formData, codigo_postal: e.target.value })}
                  placeholder="Ej: 46010"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Comentarios</Label>
                <Textarea
                  value={formData.comentarios}
                  onChange={(e) => setFormData({ ...formData, comentarios: e.target.value })}
                  placeholder="Notas adicionales..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={saving} data-testid="save-client-button">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : editingId ? (
                  "Actualizar"
                ) : (
                  "Crear"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el cliente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
